import React, { FC, useState } from 'react';
import { T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import styled from 'styled-components';
import { merchantProduct_item$data } from '../../state/queries/__generated__/merchantProduct_item.graphql';
import StoreCatalog from '../../storeCatalog/storeCatalog';
import { Dialog, DialogWindow, QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { ValidationBox } from '../../../../widgets/validation/validationBox';
import { useValidationContext, ValidationStateProvider } from 'src/shared/validation';

const CustomDialogWindow = styled(DialogWindow)`
    flex: 1;
    align-self: stretch;
    justify-self: stretch;
    margin: 0 80px;
`;

const DuplicateDialogContent: FC<{
    sourceProductCode: string;
    salesChannelID: string;
    onConfirm: (skus: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>[]) => void;
}> = ({ sourceProductCode, salesChannelID, onConfirm }) => {
    const [selectedProducts, setSelectedProducts] = useState<Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>[]>([]);
    const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>();
    const { showDialog, closeDialog } = useDialogManager();
    const validationContext = useValidationContext();

    const handleProductClick = async (merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => {
        if (selectedProducts.findIndex((x) => x.sku === merchantProduct.sku!) === -1) {
            setSelectedProducts([...selectedProducts, merchantProduct]);
        } else {
            setSelectedProducts(selectedProducts.filter((x) => x.sku !== merchantProduct.sku));
        }
    };

    const confirmProducts = (products: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>[]) => {
        if (products.length === 0) {
            setSelectedProducts([]);
            setTimeout(() => validationContext.submit(), 0);
            return;
        }

        onConfirm(products);
    };

    const handleDuplicateClick = () => {
        if (!validationContext.submit()) return;

        if (selectedProducts.some((product) => product.isConfiguratedInZakeke))
            showDialog(
                'duplication-confirm',
                <QuestionDialog
                    message={
                        <span>
                            {T._(
                                'ATTENTION: you have selected one or more products already configured as customizable. If you proceed, the new personalization settings will overwrite the existing ones. Are you sure to proceed with all the selected products?',
                                'Admin'
                            )}
                        </span>
                    }
                    onYesClick={() => {
                        closeDialog('duplication-confirm');
                        confirmProducts(selectedProducts);
                    }}
                    onNoClick={() => {
                        const filteredProducts = selectedProducts.filter((product) => !product.isConfiguratedInZakeke);
                        closeDialog('duplication-confirm');
                        confirmProducts(filteredProducts);
                    }}
                    buttonYesLabel={T._('YES, duplicate on all selected products.', 'Admin')}
                    buttonNoLabel={T._('NO, duplicate only on not configured products.', 'Admin')}
                />
            );
        else confirmProducts(selectedProducts);
    };

    return (
        <Dialog
            windowDecorator={CustomDialogWindow}
            buttons={[{ label: T._('Next', 'Admin'), onClick: handleDuplicateClick }]}
            ref={(instance) => setScrollContainer(instance)}
        >
            <ValidationBox
                fullWidth
                validationKey='duplicate-products'
                validationValue={selectedProducts.length}
                validate={(value) => value === 0}
                validationMessage={T._('At least one product is required.', 'Admin')}
            >
                <StoreCatalog
                    cardTitle={T._('Select the products you want to duplicate on', 'Admin')}
                    salesChannelID={salesChannelID}
                    onProductClick={handleProductClick}
                    scrollContainer={scrollContainer}
                    selectedProductsSKUs={selectedProducts.map((x) => x.sku!)}
                    showSelectionCheckboxes
                    showHeader={false}
                    showBackButton={false}
                    disableProductCode={sourceProductCode}
                />
            </ValidationBox>
        </Dialog>
    );
};

const DuplicateDialog: FC<React.ComponentProps<typeof DuplicateDialogContent>> = (props) => (
    <ValidationStateProvider>
        <DuplicateDialogContent {...props} />
    </ValidationStateProvider>
);

export default DuplicateDialog;
